import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { GuiaSalida, GuiaSalidaRequest, GuiaSalidaState } from '../types';
import { GuiaSalidaService } from '../services/GuiaSalidaService';

interface GuiaSalidaActions {
  fetchGuias: () => Promise<void>;
  createGuia: (request: GuiaSalidaRequest) => Promise<GuiaSalida | null>;
  updateGuia: (id: number, request: GuiaSalidaRequest) => Promise<GuiaSalida | null>;
  updateEstado: (id: number, estado: string) => Promise<boolean>;
  deleteGuia: (id: number) => Promise<boolean>;
  clearError: () => void;
}

type GuiaSalidaStore = GuiaSalidaState & GuiaSalidaActions;

const initialState: GuiaSalidaState = {
  guias: [],
  loading: false,
  error: null,
};

const extractMessage = (error: unknown, fallback: string): string => {
  if (error && typeof error === 'object') {
    const maybeAxios = error as {
      response?: { data?: { message?: string } | string };
      message?: string;
    };
    const dataMsg = typeof maybeAxios.response?.data === 'string'
      ? maybeAxios.response?.data
      : maybeAxios.response?.data?.message;
    if (dataMsg) return dataMsg;
    if (maybeAxios.message) return maybeAxios.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
};

export const useGuiaSalidaStore = create<GuiaSalidaStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      fetchGuias: async () => {
        set({ loading: true, error: null });
        try {
          const guias = await GuiaSalidaService.getAll();
          set({ guias, loading: false });
        } catch (error) {
          set({
            error: extractMessage(error, 'Error al cargar Guias de Salida'),
            loading: false,
          });
        }
      },

      createGuia: async (request: GuiaSalidaRequest) => {
        set({ loading: true, error: null });
        try {
          const guia = await GuiaSalidaService.create(request);
          await get().fetchGuias();
          return guia;
        } catch (error) {
          set({
            error: extractMessage(error, 'Error al crear Guia de Salida'),
            loading: false,
          });
          return null;
        }
      },

      updateGuia: async (id: number, request: GuiaSalidaRequest) => {
        set({ loading: true, error: null });
        try {
          const guia = await GuiaSalidaService.update(id, request);
          await get().fetchGuias();
          return guia;
        } catch (error) {
          set({
            error: extractMessage(error, 'Error al actualizar Guia de Salida'),
            loading: false,
          });
          return null;
        }
      },

      updateEstado: async (id: number, estado: string) => {
        set({ loading: true, error: null });
        try {
          await GuiaSalidaService.updateEstado(id, estado);
          await get().fetchGuias();
          return true;
        } catch (error) {
          set({
            error: extractMessage(error, 'Error al cambiar estado'),
            loading: false,
          });
          return false;
        }
      },

      deleteGuia: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await GuiaSalidaService.delete(id);
          await get().fetchGuias();
          return true;
        } catch (error) {
          set({
            error: extractMessage(error, 'Error al eliminar Guia de Salida'),
            loading: false,
          });
          return false;
        }
      },

      clearError: () => set({ error: null }),
    }),
    { name: 'guia-salida-store' }
  )
);
